/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Companion screen to LinkingPane — the new-device half. Hosts mount
// this on the sign-in / unauthenticated flow when the user picks
// "Link to existing account" instead of an OTP sign-in. The pane:
//
//   1. Asks the host to mint an X25519 link keypair + an ephemeral
//      SDK identity and a fresh KeyPackage; persists the priv key.
//   2. Renders a handshake QR (deviceId + pubkey + KP) for the user
//      to scan with an already-signed-in device.
//   3. Accepts the resulting ticket id via paste or scan, hands it
//      back to the host to consume.
//
// The pane is platform-agnostic: web wires `qrcode` + `html5-qrcode`,
// desktop wires `react-native-qrcode-svg` (paste-only for the ticket
// id until camera scanning lands).

import { CheckCircle2, ScanLine } from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';

import {
  AuthField,
  AuthInput,
  AuthLinkButton,
  AuthPrimaryButton,
} from '../../auth/AuthLayout';
import { isTicketPayload, type LinkingHandshakePayload } from './LinkingPane';

export interface LinkDevicePairingHandshake {
  readonly deviceIdBase64Url: string;
  readonly pubkeyBase64Url: string;
  readonly keyPackageBase64Url: string;
}

/**
 * Payload the host hands back to the pane once it has either received
 * a pre-auth WS push or fetched the ticket via the legacy `ticket_id`
 * GET. The pane is platform-agnostic and does no crypto itself — it
 * just shuttles the bytes through to `onRedeemSealed` so the host
 * can HPKE-open both halves.
 *
 * `authSealed` may be a zero-length array when the existing device
 * is on a legacy client that doesn't ship the auth envelope (the new
 * device then falls back to OTP-after-link rather than the
 * `/v1/auth/link-verify` short-circuit).
 */
export interface LinkDeviceSealedPayload {
  readonly sealed: Uint8Array;
  readonly authSealed: Uint8Array;
}

export interface LinkDevicePairingPaneProps {
  /**
   * Host generates the pairing material. Implementation outline:
   *   const { publicKey, privateKey } = generateKemKeyPair();   // hpke.ts
   *   persistLinkingPriv(privateKey);                            // session/AsyncStorage
   *   const identityExport = await MessagingClient.generateIdentity();
   *   await persistIdentity(identityExport);                     // platform store
   *   const client = await MessagingClient.init(identityExport, ...);
   *   const kp     = await client.freshKeyPackage();
   *   const devId  = await client.deviceId();
   *   return { deviceIdBase64Url, pubkeyBase64Url, keyPackageBase64Url };
   * Failures bubble up and surface to the user.
   */
  readonly onGenerateHandshake: () => Promise<LinkDevicePairingHandshake>;

  /**
   * Host fetches the sealed ticket, HPKE-opens it with the priv key
   * persisted in step 1, and hands the cleartext ticket to the SDK
   * via `client.consumeLinkingTicket(...)`. Resolves on success.
   */
  readonly onRedeemTicket: (ticketId: string) => Promise<void>;

  /**
   * Optional pre-auth WS push path. When supplied, the pane awaits
   * the resolved sealed bytes and calls back into the host's
   * `onRedeemSealed` to HPKE-open + consume in one shot — no
   * ticket-id paste/scan needed. A timeout or socket error causes
   * the pane to fall back to the legacy ticket-id input which
   * remains rendered. Hosts that don't wire pre-auth WS simply
   * omit both fields; behaviour is unchanged.
   */
  readonly waitForSealedPush?: () => Promise<LinkDeviceSealedPayload>;
  readonly onRedeemSealed?: (payload: LinkDeviceSealedPayload) => Promise<void>;

  readonly renderQrCode?: (value: string, size: number) => JSX.Element;
  readonly onScanRequest?: () => Promise<string | null>;

  /** Called after the new device has successfully consumed a ticket. */
  readonly onPairingComplete?: () => void;
}

export function LinkDevicePairingPane(props: LinkDevicePairingPaneProps): JSX.Element {
  const {
    onGenerateHandshake,
    onRedeemTicket,
    waitForSealedPush,
    onRedeemSealed,
    renderQrCode,
    onScanRequest,
    onPairingComplete,
  } = props;

  const [handshake, setHandshake] = useState<LinkDevicePairingHandshake | null>(null);
  const [generating, setGenerating] = useState(true);
  const [genError, setGenError] = useState<string | null>(null);

  const [ticketIdRaw, setTicketIdRaw] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // The pane shows the "auto-pair" copy whenever the host wired
  // pre-auth WS push at all — driven by intent, not runtime state.
  // If the underlying WS errors transiently (deployment in
  // progress, brief network blip) the host re-subscribes in the
  // background; bouncing the headline copy back to the legacy
  // pairing-code wording on every blip is worse UX than the
  // promise the feature makes ("scan once, you're paired").
  const preAuthWsWired =
    waitForSealedPush !== undefined && onRedeemSealed !== undefined;
  // Collapsible fallback affordance: when pre-auth WS is wired,
  // the pairing-code form is hidden behind a "Having trouble?"
  // disclosure so the user sees a single-step flow by default and
  // only reaches for the manual code if the push doesn't arrive.
  // When pre-auth WS isn't wired (legacy two-step path) the form
  // is always visible because it IS the primary affordance.
  const [showFallback, setShowFallback] = useState(false);
  const fallbackVisible = !preAuthWsWired || showFallback;

  useEffect(() => {
    let cancelled = false;
    setGenerating(true);
    setGenError(null);
    void (async () => {
      try {
        const result = await onGenerateHandshake();
        if (cancelled) return;
        setHandshake(result);
      } catch (err) {
        if (cancelled) return;
        setGenError((err as Error).message);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onGenerateHandshake]);

  // Pre-auth WS push: kick off as soon as the handshake is generated
  // so the WS is listening BEFORE the existing device scans the QR.
  // The host's `waitForSealedPush` is expected to retry transient
  // failures internally — if it rejects the pane just stays on the
  // auto-pair copy (the ticket-id field below remains the
  // always-available fallback) so a blip doesn't bounce the user
  // back to the legacy two-step wording.
  useEffect(() => {
    if (handshake === null) return;
    if (waitForSealedPush === undefined || onRedeemSealed === undefined) return;
    let cancelled = false;
    // Diagnostic console.* calls — left at info level (NOT the host
    // logger) so they show up even on prod builds where the logger
    // might be muted. Removing them once the pairing flow has been
    // stable in prod for a release cycle.
    // eslint-disable-next-line no-console
    console.info('[ping-link] subscribing to pre-auth WS');
    void (async () => {
      try {
        const payload = await waitForSealedPush();
        // eslint-disable-next-line no-console
        console.info('[ping-link] sealed bytes received', {
          sealedBytes: payload.sealed.byteLength,
          authSealedBytes: payload.authSealed.byteLength,
        });
        if (cancelled) return;
        setPending(true);
        try {
          await onRedeemSealed(payload);
          // eslint-disable-next-line no-console
          console.info('[ping-link] consumeLinkingTicket OK');
          if (cancelled) return;
          setDone(true);
          onPairingComplete?.();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[ping-link] redeem failed', err);
          if (cancelled) return;
          setError((err as Error).message);
        } finally {
          if (!cancelled) setPending(false);
        }
      } catch (wsErr) {
        // WS gave up after retries — silently fall back; the
        // ticket-id field below is already rendered.
        // eslint-disable-next-line no-console
        console.warn('[ping-link] pre-auth WS gave up', wsErr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handshake, waitForSealedPush, onRedeemSealed, onPairingComplete]);

  const submit = useCallback(
    async (ticketId: string) => {
      setPending(true);
      setError(null);
      try {
        await onRedeemTicket(ticketId);
        setDone(true);
        onPairingComplete?.();
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setPending(false);
      }
    },
    [onRedeemTicket, onPairingComplete],
  );

  const handleScan = useCallback(async () => {
    if (!onScanRequest) return;
    const text = await onScanRequest();
    if (text == null) return;
    let ticketId = text.trim();
    try {
      const parsed = JSON.parse(ticketId);
      if (isTicketPayload(parsed)) ticketId = parsed.t;
    } catch {
      // bare string is fine
    }
    void submit(ticketId);
  }, [onScanRequest, submit]);

  const handlePaste = useCallback(() => {
    if (!ticketIdRaw.trim()) {
      setError('Paste the ticket id from the existing device.');
      return;
    }
    void submit(ticketIdRaw.trim());
  }, [ticketIdRaw, submit]);

  const handshakeQrValue =
    handshake !== null
      ? JSON.stringify({
          v: 1,
          deviceId: handshake.deviceIdBase64Url,
          pubkey: handshake.pubkeyBase64Url,
          keyPackage: handshake.keyPackageBase64Url,
        } satisfies LinkingHandshakePayload)
      : null;

  return (
    <View className="flex-col">
      {/* Hero QR — the centerpiece. WhatsApp puts the QR front-and-
          centre and folds the instructions around it; we follow the
          same idiom inside the auth 400px column. The card is white
          on every theme (QR contrast requires it) with a subtle
          accent-brand glow ring to anchor it visually. */}
      <View className="items-center mb-xl">
        <View
          className="
            p-[20px] rounded-[20px] bg-white
            border border-light-border-primary dark:border-[#2F333B]
          "
          style={
            {
              boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.04) inset',
            } as unknown as import('react-native').ViewStyle
          }
        >
          {generating ? (
            <View className="w-[224px] h-[224px] items-center justify-center">
              <ActivityIndicator />
              <Text className="font-sans text-[11px] leading-[15px] text-[#6B7280] mt-sm">
                Generating link keys…
              </Text>
            </View>
          ) : genError !== null ? (
            <View className="w-[224px] h-[224px] items-center justify-center px-md">
              <Text className="font-sans text-[12px] leading-[17px] text-[#FF5353] text-center">
                {genError}
              </Text>
            </View>
          ) : handshake !== null && handshakeQrValue !== null && renderQrCode !== undefined ? (
            renderQrCode(handshakeQrValue, 224)
          ) : (
            <View className="w-[224px] h-[224px] items-center justify-center">
              <Text className="font-mono text-[10px] leading-[14px] text-[#6B7280] text-center break-all">
                {handshakeQrValue ?? '—'}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Numbered instruction list. Same circular badge + body-text
          idiom WhatsApp uses (and analogous to how iOS Continuity sets
          up its "Trust This Computer" handshake). When the host opted
          into pre-auth WS push (`waitForSealedPush` supplied), step 3
          says "you're done" because the new device receives the sealed
          ticket automatically. Otherwise we fall back to the legacy
          ticket-id paste/scan path the field below covers. */}
      <View className="flex-col mb-xl">
        <NumberedStep
          index={1}
          body={
            <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
              Open Ping on a device you're already signed in to.
            </Text>
          }
        />
        <NumberedStep
          index={2}
          body={
            <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
              Go to Settings → Linked devices, then scan the QR above.
            </Text>
          }
        />
        <NumberedStep
          index={3}
          body={
            preAuthWsWired ? (
              <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
                That's it — this device will pair automatically once the QR is scanned.
              </Text>
            ) : (
              <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
                That device shows a pairing code — paste it below or tap{' '}
                <Text className="font-sans font-medium text-[14px] leading-[20px] text-light-accent-brand dark:text-dark-accent-brand">
                  Scan pairing code
                </Text>
                .
              </Text>
            )
          }
        />
      </View>

      {/* Pre-auth WS status banner. Without this, when `onRedeemSealed`
          throws (e.g. the SDK rejects the ticket, the priv key is
          missing from sessionStorage, consumeLinkingTicket trips on a
          provider error) the user sees no UI change at all — `error`
          + `pending` were previously only rendered inside the
          collapsed pairing-code fallback. Surface them next to the
          QR so a failed auto-pair doesn't masquerade as "still
          waiting". `done` is shown so the user sees the transition
          before App.tsx swings into the bootstrap path. */}
      {preAuthWsWired && (pending || error !== null || done) && (
        <View
          className={
            'mb-md rounded-lg px-md py-sm ' +
            (error !== null
              ? 'bg-light-accent-rose/10 dark:bg-dark-accent-rose/10'
              : done
              ? 'bg-light-accent-success/10 dark:bg-dark-accent-success/10'
              : 'bg-light-surface-secondary dark:bg-dark-surface-secondary')
          }
        >
          {error !== null ? (
            <Text className="font-sans text-[13px] leading-[18px] text-light-accent-rose dark:text-dark-accent-rose">
              {`Pairing failed: ${error}. Try the pairing code below.`}
            </Text>
          ) : done ? (
            <Text className="font-sans text-[13px] leading-[18px] text-light-text-primary dark:text-dark-text-primary">
              Paired — finishing sign-in…
            </Text>
          ) : (
            <View className="flex-row items-center gap-sm">
              <ActivityIndicator />
              <Text className="font-sans text-[13px] leading-[18px] text-light-text-primary dark:text-dark-text-primary">
                Pairing…
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Pairing-code fallback. When pre-auth WS is wired this is
          collapsed behind a "Having trouble?" disclosure so the
          single-step path is visually uncluttered; otherwise (legacy
          two-step hosts) it's rendered inline as the primary input. */}
      {!fallbackVisible ? (
        <View className="items-center">
          <Pressable
            onPress={() => setShowFallback(true)}
            accessibilityRole="button"
            accessibilityLabel="ping_link_show_fallback"
            className="px-md py-sm rounded-pill active:opacity-70"
          >
            <Text className="font-sans font-normal text-[13px] leading-[18px] text-light-text-secondary dark:text-dark-text-secondary underline">
              Having trouble? Enter a pairing code instead
            </Text>
          </Pressable>
        </View>
      ) : (
        <>
          <AuthField label="Pairing code">
            <AuthInput
              value={ticketIdRaw}
              onChangeText={setTicketIdRaw}
              placeholder="Paste the pairing code"
              autoCapitalize="none"
              accessibilityLabel="ping_link_ticket_id"
            />
          </AuthField>

          {error !== null && (
            <Text className="font-sans text-[12px] leading-[17px] text-light-accent-rose dark:text-dark-accent-rose mt-xs">
              {error}
            </Text>
          )}

          <View className="h-lg" />

          <AuthPrimaryButton
            onPress={handlePaste}
            disabled={pending || ticketIdRaw.length === 0}
            accessibilityLabel="ping_link_complete"
          >
            {pending ? 'Linking…' : 'Complete pairing'}
          </AuthPrimaryButton>

          {onScanRequest !== undefined && (
            <View className="items-center mt-md">
              <Pressable
                onPress={handleScan}
                disabled={pending}
                accessibilityRole="button"
                accessibilityLabel="ping_link_scan_ticket"
                className="flex flex-row items-center gap-2 px-md py-sm rounded-pill active:opacity-70"
              >
                <ScanLine size={16} strokeWidth={1.75} color="currentColor" />
                <Text className="font-sans font-medium text-[14px] leading-[20px] text-light-accent-brand dark:text-dark-accent-brand">
                  Scan pairing code
                </Text>
              </Pressable>
            </View>
          )}
        </>
      )}

      {done && (
        <View className="flex flex-row items-center gap-2 mt-lg justify-center">
          <CheckCircle2 size={16} strokeWidth={1.75} color="#3CCB4F" />
          <Text className="font-sans font-medium text-[14px] leading-[20px] text-light-accent-success dark:text-dark-accent-success">
            Device linked.
          </Text>
        </View>
      )}
    </View>
  );
}

interface NumberedStepProps {
  readonly index: number;
  readonly body: JSX.Element;
}

/**
 * Numbered-list row: brand-coloured circular index badge on the left,
 * body text on the right. Spacing tracks WhatsApp's "Scan to log in"
 * screen (~12px between rows, 12px between badge and body) and uses
 * our auth design tokens so it picks up theme + accent automatically.
 */
function NumberedStep({ index, body }: NumberedStepProps): JSX.Element {
  return (
    <View className="flex flex-row items-start gap-sm mb-sm">
      <View className="w-[24px] h-[24px] rounded-full bg-light-accent-brand dark:bg-dark-accent-brand items-center justify-center mt-[1px]">
        <Text className="font-sans font-bold text-[12px] leading-[12px] text-light-text-onAccent dark:text-dark-text-onAccent">
          {index}
        </Text>
      </View>
      <View className="flex-1">{body}</View>
    </View>
  );
}
// `AuthLinkButton` is exported for the host but not used inside this
// pane today — kept in the barrel so callers don't have to import from
// the lower-level `AuthLayout` path directly.
void AuthLinkButton;
